import { Injectable } from '@angular/core';
import { MainMapService } from './main-map.service';
import LayerList from '@arcgis/core/widgets/LayerList.js';
import MapView from '@arcgis/core/views/MapView';
import TileLayer from '@arcgis/core/layers/TileLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import ImageryLayer from '@arcgis/core/layers/ImageryLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import esriConfig from '@arcgis/core/config.js';
import { onlinePortalUrl, urlHacrazotLayer, urlGushAndParcelLayer } from '../constants/constants';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/auth.service';
import { LanguageService } from 'src/app/services/language.service';
import { createHacrazotPopupTemplate } from '../popupTemplates/hacrazotPopupTemplate';
import { createPopupTemplate } from '../popupTemplates/genericPopupTemplate';
import { createGushParcelPopupTemplate } from '../popupTemplates/gushParcelPopupTemplate';

@Injectable({
  providedIn: 'root'
})
export class SearchAdvancedMapService extends MainMapService {
  
  constructor(translate: TranslateService, authService: AuthService, languageService: LanguageService) {
    super(translate, authService, languageService);
  }
  
  override updatePopupTemplates(): void {
    if (!this.currentMapView) return;
    
    const hacrazotLayer = this.currentMapView.map?.findLayerById('hacrazot-layer') as FeatureLayer;
    if (hacrazotLayer) {
      hacrazotLayer.popupTemplate = createHacrazotPopupTemplate(this.translate);
    }

    // Update gush and parcel layer popup template and title
    const currentLang = this.languageService.currentLanguage.getValue() as 'he' | 'en';
    const gushAndParcelLayer = this.currentMapView.map?.findLayerById('gush-and-parcel-layer') as FeatureLayer;
    if (gushAndParcelLayer) {
      const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');
      gushAndParcelLayer.popupTemplate = createGushParcelPopupTemplate(layerTitle, currentLang, this.internalUser) as any;
      
      if (!this.internalUser) {
        const disclaimer = this.translate.instant('iaa.map.gushAndParcelDisclaimer');
        gushAndParcelLayer.title = `${layerTitle} - ${disclaimer}`;
      }
    }
  }

  override onLayerListReady(layerList: LayerList): void {
    		let widget = layerList;
		
		if (layerList && (layerList as any).widget) {
			widget = (layerList as any).widget;
		}
    this.configureLayerList(widget);
    if (layerList.view) {
      this.populateSearchAdvancedLayers(layerList.view as MapView);
    }
  }

  private populateSearchAdvancedLayers(view: MapView): void {
	if(this.internalUser) {
    const prefixPortalUrlMapServices = 'https://gis.israntique.org.il/arcgis/rest/services/';
    
    // Check if basemap group already exists to avoid duplicates
    let baseMapGroupLayer = view.map?.findLayerById('basemap-group-layer') as GroupLayer;
    if (!baseMapGroupLayer) {
      var israelBasemap = new TileLayer({
        url: prefixPortalUrlMapServices + 'MapCacheNational_2019/MapServer',
      });
      israelBasemap.title = 'מפת רקע מדינת ישראל';

      var peilutTileIsraelOrtophoto2021Basemap20CM = new ImageryLayer({
        url: 'https://gis.israntique.org.il/image/rest/services/MAPI/MosaicMAPI2021Ortho20CM/ImageServer',
        title: 'מפת תצא 2021 מפי 20 סמ',
        visible: false
      });

      var peilutTileIsraelOrtophoto2021Basemap50CM = new ImageryLayer({
        url: 'https://gis.israntique.org.il/image/rest/services/MAPI/MosaicMAPI2021Ortho50CM/ImageServer',
        title: 'מפת תצא 2021 מפי 50 סמ',
        visible: false
      });

      baseMapGroupLayer = new GroupLayer({
        id: 'basemap-group-layer',
        title: 'מפות רקע מדינת ישראל',
        visible: true,
        visibilityMode: 'exclusive',
        layers: [
          peilutTileIsraelOrtophoto2021Basemap50CM,
          peilutTileIsraelOrtophoto2021Basemap20CM,
          israelBasemap,
        ],
      });
      // Add basemap at index 0 (bottom)
      view.map?.layers.add(baseMapGroupLayer, 0);
    }
  } else {
    // For external users, check if hacrazot layer already exists
    let hacrazotLayer = view.map?.findLayerById('hacrazot-layer');
    if (!hacrazotLayer) {
      esriConfig.apiKey = (window as any).gis.esriApiKey;
      esriConfig.portalUrl = onlinePortalUrl;
      hacrazotLayer = new FeatureLayer({
        id: 'hacrazot-layer',
        url: urlHacrazotLayer,
        outFields: ['*'],
        popupTemplate: createHacrazotPopupTemplate(this.translate),
        title: this.translate.instant('iaa.map.hachrazaLayer'),
        opacity: 0.5,
        visible: false,
      });
      view.map?.layers.add(hacrazotLayer);
    }
  }

  // Add gush and parcel layer for both internal and external users
  let gushAndParcelLayer = view.map?.findLayerById('gush-and-parcel-layer');
  if (!gushAndParcelLayer) {
    const currentLang = this.languageService.currentLanguage.getValue() as 'he' | 'en';
    const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');
    const disclaimer = this.internalUser ? '' : ` - ${this.translate.instant('iaa.map.gushAndParcelDisclaimer')}`;
    const layerUrl = this.internalUser 
      ? 'https://gis.israntique.org.il/arcgis/rest/services/GIS_CENTRAL/GUSH_AND_PARCEL/MapServer/0'
      : urlGushAndParcelLayer;
    
    gushAndParcelLayer = new FeatureLayer({
      id: 'gush-and-parcel-layer',
      url: layerUrl,
      outFields: ['*'],
      popupTemplate: createGushParcelPopupTemplate(layerTitle, currentLang, this.internalUser) as any,
      title: `${layerTitle}${disclaimer}`,
      opacity: 0.4,
      visible: false,
      maxScale: 0,
      minScale: 1000000,
      labelsVisible: false,
    });
    // Add gush-parcel layer at index 1 (above basemap) for internal users, index 0 for external
    view.map?.layers.add(gushAndParcelLayer, this.internalUser ? 1 : 0);
  }
}
}